<template>
  <div class="bar-chart" ref="chart" style="width: 100%; height: 100%"></div>
</template>

<script>
export default {
  name: "BarChart",
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    displayCount: {
      type: Number,
      default: 5,
    },
    chartData: {
      type: Object,
      default: null,
    },
    showLegend: {
      type: Boolean,
      default: false,
    },
    yAxisFormatter: {
      type: Function,
      default: null,
    },
    showXAxisLabel: {
      type: Boolean,
      default: true,
    },
    xAxisLabelMaxLength: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      chart: null,
    };
  },
  watch: {
    data: {
      handler() {
        this.updateChart();
      },
      deep: true,
    },
    chartData: {
      handler() {
        this.updateChart();
      },
      deep: true,
    },
    showXAxisLabel() {
      this.updateChart();
    },
    xAxisLabelMaxLength() {
      this.updateChart();
    },
  },
  mounted() {
    this.initChart();
    window.addEventListener("resize", this.handleResize);
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
    window.removeEventListener("resize", this.handleResize);
  },
  methods: {
    initChart() {
      this.chart = this.$echarts.init(this.$refs.chart);
      this.$nextTick(() => {
        this.updateChart();
        this.handleResize();
      });
    },
    updateChart() {
      if (!this.chart) return;

      let option;

      if (this.chartData) {
        const xData = this.chartData.categories || [];
        const series = (this.chartData.series || []).map((s) => {
          let colorOpt = s.color;
          if (Array.isArray(s.color) && s.color.length === 2) {
            colorOpt = new this.$echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: s.color[0] },
              { offset: 1, color: s.color[1] },
            ]);
          }
          return {
            name: s.name,
            type: "bar",
            barWidth: s.barWidth || "12px",
            barGap: "30%",
            itemStyle: {
              color: colorOpt,
              borderRadius: [4, 4, 0, 0],
            },
            label: s.showLabel
              ? {
                  show: true,
                  position: "top",
                  color: "#333",
                  fontSize: 12,
                  formatter: (params) => Number(params.value).toLocaleString(),
                }
              : { show: false },
            data: s.data,
          };
        });

        option = {
          tooltip: {
            trigger: "axis",
            axisPointer: { type: "shadow" },
            confine: true,
          },
          legend: this.showLegend
            ? {
                top: 0,
                right: 0,
                icon: "roundRect",
                itemWidth: 16,
                itemHeight: 4,
                textStyle: { color: "#666", fontSize: 12 },
              }
            : undefined,
          grid: {
            left: "3%",
            right: "4%",
            bottom: 20,
            top: this.showLegend ? "15%" : "3%",
            containLabel: true,
          },
          dataZoom: [
            {
              type: "slider",
              show: true,
              xAxisIndex: [0],
              startValue: 0,
              endValue: this.displayCount - 1,
              height: 8,
              bottom: 5,
              fillerColor: "#c5c5c5",
              borderColor: "transparent",
              backgroundColor: "#f1f1f1",
              handleSize: 0,
              showDetail: false,
              showDataShadow: false,
            },
            {
              type: "inside",
              xAxisIndex: [0],
              startValue: 0,
              endValue: this.displayCount - 1,
            },
          ],
          xAxis: {
            type: "category",
            data: xData,
            axisTick: { show: false },
            axisLine: { show: false },
            axisLabel: {
              show: this.showXAxisLabel,
              interval: 0,
              color: "#666",
              fontSize: 12,
              margin: 15,
              formatter: (value) => {
                if (
                  this.xAxisLabelMaxLength &&
                  typeof value === "string" &&
                  value.length > this.xAxisLabelMaxLength
                ) {
                  return value.slice(0, this.xAxisLabelMaxLength) + "...";
                }
                return value;
              },
            },
          },
          yAxis: {
            type: "value",
            splitLine: {
              show: true,
              lineStyle: { type: "dashed", color: "#EEEEEE" },
            },
            axisLabel: {
              color: "#999",
              formatter: this.yAxisFormatter
                ? this.yAxisFormatter
                : (value) => value,
            },
          },
          series: series,
        };
      } else {
        const xData = this.data.map((item) => item.name);
        const seriesData = this.data.map((item) => item.value);

        option = {
          tooltip: {
            trigger: "axis",
            axisPointer: {
              type: "shadow",
            },
            confine: true,
          },
          grid: {
            left: "3%",
            right: "4%",
            bottom: 20,
            top: "3%",
            containLabel: true,
          },
          dataZoom: [
            {
              type: "slider",
              show: true,
              xAxisIndex: [0],
              startValue: 0,
              endValue: this.displayCount - 1,
              height: 8,
              bottom: 5,
              fillerColor: "#c5c5c5",
              borderColor: "transparent",
              backgroundColor: "#f1f1f1",
              handleSize: 0,
              showDetail: false,
              showDataShadow: false,
            },
            {
              type: "inside",
              xAxisIndex: [0],
              startValue: 0,
              endValue: this.displayCount - 1,
            },
          ],
          xAxis: {
            type: "category",
            data: xData,
            axisTick: {
              show: false,
            },
            axisLine: {
              show: false,
            },
            axisLabel: {
              show: this.showXAxisLabel,
              interval: 0,
              color: "#666",
              fontSize: 12,
              margin: 15,
              formatter: (value) => {
                if (
                  this.xAxisLabelMaxLength &&
                  typeof value === "string" &&
                  value.length > this.xAxisLabelMaxLength
                ) {
                  return value.slice(0, this.xAxisLabelMaxLength) + "...";
                }
                return value;
              },
            },
          },
          yAxis: {
            type: "value",
            splitLine: {
              show: true,
              lineStyle: {
                type: "dashed",
                color: "#EEEEEE",
              },
            },
            axisLabel: {
              color: "#999",
              formatter: this.yAxisFormatter
                ? this.yAxisFormatter
                : (value) => {
                    if (value === 0) return "0";
                    return value / 10000 + "w";
                  },
            },
          },
          series: [
            {
              type: "bar",
              barWidth: "32px",
              itemStyle: {
                color: new this.$echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: "#70A4F8" },
                  { offset: 1, color: "#3B7BDF" },
                ]),
                borderRadius: [6, 6, 0, 0],
              },
              label: {
                show: true,
                position: "top",
                color: "#333",
                fontSize: 12,
                formatter: (params) => {
                  return Number(params.value).toLocaleString();
                },
              },
              data: seriesData,
            },
          ],
        };
      }
      this.chart.clear();
      this.chart.setOption(option, true);
      this.$nextTick(this.handleResize);
    },
    handleResize() {
      if (this.chart) {
        this.chart.resize();
      }
    },
  },
};
</script>

<style scoped>
.bar-chart {
  width: 100%;
  height: 100%;
  min-height: 250px;
}
</style>
